/**
 * Portfolios page - investment thesis groupings management
 */
import { useState } from 'react'
import {
  usePortfolios,
  useCreatePortfolio,
  useUpdatePortfolio,
  useDeletePortfolio,
} from '../hooks'
import { Button, Input, Modal, LoadingCard } from '../components/common'
import type { Portfolio, PortfolioCreate, PortfolioUpdate } from '../types'

// Predefined colors for portfolios
const PORTFOLIO_COLORS = [
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F59E0B' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Teal', value: '#14B8A6' },
]

export function PortfoliosPage() {
  const { data: portfolios, isLoading } = usePortfolios()
  const createPortfolio = useCreatePortfolio()
  const updatePortfolio = useUpdatePortfolio()
  const deletePortfolio = useDeletePortfolio()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null)
  const [formData, setFormData] = useState<PortfolioCreate>({
    name: '',
    description: '',
    color: PORTFOLIO_COLORS[0].value,
  })
  const [error, setError] = useState('')

  const openCreateModal = () => {
    setEditingPortfolio(null)
    setFormData({
      name: '',
      description: '',
      color: PORTFOLIO_COLORS[0].value,
    })
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio)
    setFormData({
      name: portfolio.name,
      description: portfolio.description || '',
      color: portfolio.color || PORTFOLIO_COLORS[0].value,
    })
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (editingPortfolio) {
        const update: PortfolioUpdate = {
          name: formData.name,
          description: formData.description || null,
          color: formData.color || null,
        }
        await updatePortfolio.mutateAsync({
          portfolioId: editingPortfolio.portfolio_id,
          data: update,
        })
      } else {
        await createPortfolio.mutateAsync(formData)
      }
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    }
  }

  const handleDelete = async (portfolioId: string) => {
    if (!confirm('Are you sure you want to delete this portfolio? It must have no holdings.')) {
      return
    }

    try {
      await deletePortfolio.mutateAsync(portfolioId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete portfolio')
    }
  }

  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  if (isLoading) {
    return <LoadingCard />
  }

  // Sort portfolios alphabetically
  const sortedPortfolios = [...(portfolios || [])].sort((a, b) => a.name.localeCompare(b.name))

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Portfolios</h1>
          <p className="mt-1 text-sm text-gray-500">
            Organize holdings by investment thesis
          </p>
        </div>
        <Button onClick={openCreateModal}>Create Portfolio</Button>
      </div>

      {/* Portfolios Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedPortfolios.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow px-6 py-8 text-center text-gray-500">
            No portfolios yet. Create one to organize your holdings.
          </div>
        ) : (
          sortedPortfolios.map((portfolio) => (
              <div
                key={portfolio.portfolio_id}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: portfolio.color || '#9CA3AF' }}
                      />
                      <h3 className="text-lg font-medium text-gray-900">
                        {portfolio.name}
                      </h3>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openEditModal(portfolio)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(portfolio.portfolio_id)}
                        className="text-gray-400 hover:text-red-600 p-1"
                        title="Delete"
                        disabled={(portfolio.holdings_count || 0) > 0}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {portfolio.description && (
                    <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                      {portfolio.description}
                    </p>
                  )}

                  <div className="mt-4 flex justify-between text-sm">
                    <div>
                      <span className="text-gray-500">Holdings</span>
                      <p className="font-medium text-gray-900">
                        {portfolio.holdings_count || 0}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-gray-500">Total Cost</span>
                      <p className="font-medium text-gray-900">
                        {formatCurrency(portfolio.total_cost)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
          ))
        )}
      </div>

      {/* Create/Edit Portfolio Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPortfolio ? 'Edit Portfolio' : 'Create Portfolio'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., AI Infrastructure"
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Investment thesis or strategy..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {PORTFOLIO_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color.value
                      ? 'border-gray-900 ring-2 ring-offset-2 ring-gray-900'
                      : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData({ ...formData, color: color.value })}
                  title={color.name}
                />
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createPortfolio.isPending || updatePortfolio.isPending}
            >
              {editingPortfolio ? 'Save Changes' : 'Create Portfolio'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
